import React from 'react';
import { db, storage } from '../firebase';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, setDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { signOutUser, useAuth } from '../auth';
import { useToast } from '../components/Toast';
import ImageField from '../components/ImageField';
import { clearAllCaches } from '../utils/cache';
import { optimizeImage, formatBytes } from '../utils/imageOptimizer';
import { scanStorageImages, optimizeExistingImage } from '../utils/bulkImageOptimizer';
import { getDefaultCategoryImage, getDefaultMenuPageImages, resolveCategoryImage, resolveMenuPageImages, PLACEHOLDER_IMAGE } from '../utils/categoryImages';
import { parseMenuData, importMenuToFirestore, clearAllMenuData, importManualMenuJsonToFirestore } from '../utils/menuImporter';
import manualMenuSeed from '../data/manualMenu_soups_breakfast_salads.json';
import manualMenuAppetizers from '../data/manualMenu_appetizers.json';
import manualMenuCatsMore from '../data/manualMenu_categories_grill_international_sides_pasta.json';
import manualMenuBurgers from '../data/manualMenu_burgers.json';
import manualMenuDessertsDrinks from '../data/manualMenu_desserts_coffee_freakshakes_lemonades.json';
import manualMenuSoftDrinksCocktails from '../data/manualMenu_softdrinks_cocktails_mocktails.json';
import manualMenuSauces from '../data/manualMenu_sauces.json';

function sanitizeFilename(name) {
	return (name || 'image')
		.toString()
		.replace(/[^a-zA-Z0-9._-]+/g, '_')
		.replace(/^_+|_+$/g, '')
		.slice(0, 120) || 'image';
}

// Upload budgets per slot. Cards render at ~400px wide, menu pages open fullscreen.
const UPLOAD_PROFILES = {
	'menu-page-images': { maxWidth: 2000, maxHeight: 2000, targetBytes: 500 * 1024 },
	'product-images': { maxWidth: 1400, maxHeight: 1400, targetBytes: 260 * 1024 },
	'category-images': { maxWidth: 1400, maxHeight: 1400, targetBytes: 260 * 1024 }
};

function useCategories(refresh) {
	const [categories, setCategories] = React.useState([]);
	React.useEffect(() => {
		(async () => {
			const snap = await getDocs(collection(db, 'categories'));
			const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
			list.sort((a,b)=>{
				// Safe name extraction for sorting
				const getName = (item) => {
					if (!item || !item.name) return '';
					if (typeof item.name === 'object') {
						return item.name.ro || item.name.en || item.name.ar || '';
					}
					return item.name || '';
				};
				const an = getName(a).toString().toLowerCase();
				const bn = getName(b).toString().toLowerCase();
				return an.localeCompare(bn);
			});
			setCategories(list);
		})();
	}, [refresh]);
	return categories;
}

function useProducts(refresh) {
	const [products, setProducts] = React.useState([]);
	React.useEffect(() => {
		(async () => {
			const snap = await getDocs(collection(db, 'products'));
			const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
			list.sort((a,b)=>{
				const an = (a?.name_ro || (a?.name?.ro) || (a?.name?.en) || a?.name || '').toString().toLowerCase();
				const bn = (b?.name_ro || (b?.name?.ro) || (b?.name?.en) || b?.name || '').toString().toLowerCase();
				return an.localeCompare(bn);
			});
			setProducts(list);
		})();
	}, [refresh]);
	return products;
}

function buildPathMap(categories, lang) {
	const idToCat = new Map(categories.map(c => [c.id, c]));
	const getName = (c) => {
		if (!c || !c.name) return '';
		if (typeof c.name === 'object' && c.name !== null) {
			return c.name[lang] || c.name.en || c.name.ro || c.name.ar || '';
		}
		return c.name || '';
	};
	function pathParts(cat) {
		const parts = [getName(cat)];
		const seen = new Set([cat.id]);
		let p = cat.parentId ? idToCat.get(cat.parentId) : null;
		// `seen` guards against a corrupted parent chain looping forever.
		while (p && !seen.has(p.id)) {
			seen.add(p.id);
			parts.unshift(getName(p));
			p = p.parentId ? idToCat.get(p.parentId) : null;
		}
		return parts;
	}
	return categories.map(c => {
		const parts = pathParts(c);
		return { id: c.id, label: parts.join(' / '), name: parts[parts.length - 1] || '' };
	});
}

const MENU_PAGE_MODES = new Set(['products_only', 'image_and_products', 'image_only']);

// A subcategory with no menu-page settings of its own borrows them from its parent.
// Mirrors CategoryProducts.js so the form never shows a state the site disagrees with.
function resolveInheritedMenuPage(cat, categoriesById) {
	if (!cat?.parentId || !categoriesById) return null;
	const raw = (cat?.menuPageMode || '').toString().trim();
	const ownImages = Array.isArray(cat?.menuPageImages) ? cat.menuPageImages.filter(Boolean) : [];
	if (MENU_PAGE_MODES.has(raw) || ownImages.length > 0) return null;

	const parent = categoriesById.get(cat.parentId);
	if (!parent?.menuPageInheritToChildren) return null;
	const parentImages = Array.isArray(parent?.menuPageImages) ? parent.menuPageImages.filter(Boolean) : [];
	if (parentImages.length === 0) return null;

	const childMode = (parent?.menuPageChildrenMode || '').toString().trim();
	const parentMode = (parent?.menuPageMode || '').toString().trim();
	const mode = MENU_PAGE_MODES.has(childMode)
		? childMode
		: (MENU_PAGE_MODES.has(parentMode) ? parentMode : 'image_only');
	return { parent, images: parentImages, mode };
}

// Mirrors the fallback in CategoryProducts.js: with no stored menuPageMode, a category
// that still has menu-page images renders them. The form must open on that same value,
// otherwise pressing Save would silently strip the photo off the live site.
function effectiveMenuPageMode(cat) {
	const raw = (cat?.menuPageMode || '').toString().trim();
	if (MENU_PAGE_MODES.has(raw)) return raw;
	const stored = Array.isArray(cat?.menuPageImages) ? cat.menuPageImages.filter(Boolean) : [];
	const defaults = cat?.useDefaultMenuPageImages !== false ? (getDefaultMenuPageImages(cat) || []) : [];
	return (stored.length > 0 || defaults.length > 0) ? 'image_and_products' : 'products_only';
}

const CATEGORY_LEVEL_LABELS = ['Main category', 'Subcategory', 'Sub-subcategory'];
function categoryLevelLabel(depth) {
	return CATEGORY_LEVEL_LABELS[depth] || `Level ${depth + 1}`;
}

// Depth-first ordering so the admin list mirrors the real menu hierarchy, with
// each row annotated with its depth and how many children/dishes hang off it.
function buildCategoryTree(categories, products) {
	const knownIds = new Set(categories.map(c => c.id));
	const childrenByParent = new Map();
	for (const c of categories) {
		// A parentId pointing at a deleted category means the row is really top-level.
		const pid = c.parentId && knownIds.has(c.parentId) ? c.parentId : null;
		if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
		childrenByParent.get(pid).push(c);
	}

	const dishCounts = new Map();
	for (const p of products || []) {
		if (!p?.categoryId) continue;
		dishCounts.set(p.categoryId, (dishCounts.get(p.categoryId) || 0) + 1);
	}

	const rows = [];
	const seen = new Set();
	const walk = (parentId, depth) => {
		const kids = childrenByParent.get(parentId) || [];
		kids.forEach((cat, index) => {
			if (seen.has(cat.id)) return;
			seen.add(cat.id);
			rows.push({
				cat,
				depth,
				// parentId set but pointing nowhere: the site hides this category entirely.
				orphaned: !!(cat.parentId && !knownIds.has(cat.parentId)),
				isLast: index === kids.length - 1,
				childCount: (childrenByParent.get(cat.id) || []).length,
				dishCount: dishCounts.get(cat.id) || 0
			});
			walk(cat.id, depth + 1);
		});
	};
	walk(null, 0);

	// Anything left sat in a broken parent loop; surface it instead of hiding it.
	for (const cat of categories) {
		if (seen.has(cat.id)) continue;
		seen.add(cat.id);
		rows.push({ cat, depth: 0, orphaned: true, isLast: true, childCount: 0, dishCount: dishCounts.get(cat.id) || 0 });
	}
	return rows;
}

// Every category that sits below `rootId`, so we never offer a descendant as a parent
// (that would create a cycle and hang the path builder).
function collectDescendantIds(categories, rootId) {
	const ids = new Set();
	if (!rootId) return ids;
	const childrenByParent = new Map();
	for (const c of categories) {
		const pid = c.parentId || null;
		if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
		childrenByParent.get(pid).push(c.id);
	}
	const queue = [rootId];
	while (queue.length) {
		const id = queue.shift();
		for (const kid of childrenByParent.get(id) || []) {
			if (ids.has(kid)) continue;
			ids.add(kid);
			queue.push(kid);
		}
	}
	return ids;
}

function useFooterSettings(refresh) {
	const [settings, setSettings] = React.useState(null);
	React.useEffect(() => {
		(async () => {
			const docSnap = await getDoc(doc(db, 'settings', 'footer'));
			if (docSnap.exists()) {
				setSettings(docSnap.data());
			} else {
				// Default values
				setSettings({
					address: 'Calea Vitan 174, Bucharest, Romania',
					phone: '+40 7xx xxx xxx',
					email: 'info@tress2.com',
					hoursMonThu: '11:00–22:00',
					hoursFriSat: '11:00–23:00',
					hoursSun: '12:00–21:00',
					instagramUrl: '',
					facebookUrl: '',
					tiktokUrl: ''
				});
			}
		})();
	}, [refresh]);
	return settings;
}

function useAppSettings(refresh) {
	const [settings, setSettings] = React.useState(null);
	React.useEffect(() => {
		(async () => {
			try {
				const docSnap = await getDoc(doc(db, 'settings', 'app'));
				if (docSnap.exists()) {
					setSettings(docSnap.data());
				} else {
					setSettings({
						showProductPlaceholderImage: true
					});
				}
			} catch (e) {
				console.error('Error loading app settings:', e);
				setSettings({ showProductPlaceholderImage: true });
			}
		})();
	}, [refresh]);
	return settings;
}

function LangTabs({ value, onChange, filled }) {
	return (
		<div className="flex items-center gap-2 mb-2">
			<span className="text-xs text-muted-gray">Language</span>
			<div className="flex items-center gap-1 bg-black/50 border border-gold/20 p-1 rounded-lg">
				{['ro', 'en', 'ar'].map(lng => (
					<button
						key={lng}
						type="button"
						onClick={() => onChange(lng)}
						className={`admin-tap flex items-center justify-center gap-1.5 px-3 sm:px-2.5 py-1 rounded text-xs font-semibold transition-colors ${value === lng ? 'bg-gold text-black' : 'text-off-white hover:text-gold'}`}
					>
						{lng.toUpperCase()}
						<span className={`h-1.5 w-1.5 rounded-full ${filled?.[lng] ? (value === lng ? 'bg-black/60' : 'bg-emerald-400') : 'bg-red-400/70'}`} />
					</button>
				))}
			</div>
			<span className="text-xs text-muted-gray">all 3 required</span>
		</div>
	);
}

function SectionHeading({ icon, title, subtitle, badge }) {
	return (
		<div className="flex items-start justify-between gap-3 mb-5 pb-4 border-b border-gold/15">
			<div className="flex items-start gap-3 min-w-0">
				<span className="mt-0.5 h-9 w-9 shrink-0 rounded-lg bg-gold/10 border border-gold/25 flex items-center justify-center">
					<i className={`fas ${icon} text-gold text-sm`}></i>
				</span>
				<div className="min-w-0">
					<h2 className="font-cinzel text-xl text-gold leading-tight">{title}</h2>
					{subtitle ? <p className="text-xs text-muted-gray mt-1">{subtitle}</p> : null}
				</div>
			</div>
			{badge ? (
				<span className="shrink-0 text-xs px-2.5 py-1 rounded-full bg-gold/10 text-gold border border-gold/25">{badge}</span>
			) : null}
		</div>
	);
}

// A <option> cannot be styled, so nesting is conveyed with non-breaking spaces.
function hierarchyOptionLabel(o) {
	const name = o?.name || o?.label || '';
	if (!o?.depth) return name;
	return `${'\u00A0'.repeat(o.depth * 4)}└ ${name}`;
}

function CategoryKindBadge({ depth, orphaned, className = '' }) {
	if (orphaned) {
		return (
			<span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border bg-red-500/15 text-red-300 border-red-400/40 ${className}`}>
				<i className="fas fa-triangle-exclamation"></i>
				Parent deleted — hidden
			</span>
		);
	}
	const isMain = depth === 0;
	return (
		<span
			className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${
				isMain
					? 'bg-sky-500/15 text-sky-300 border-sky-400/30'
					: 'bg-violet-500/15 text-violet-300 border-violet-400/30'
			} ${className}`}
		>
			<i className={`fas ${isMain ? 'fa-folder' : 'fa-diagram-next'}`}></i>
			{categoryLevelLabel(depth)}
		</span>
	);
}

// The two image slots confuse people, so the form spells out where each one shows up.
const TAP_BEHAVIOR_OPTIONS = [
	{
		id: 'products_only',
		icon: 'fa-list-ul',
		title: 'The dish list',
		desc: 'Normal. Shows dish cards / subcategories with names and prices.'
	},
	{
		id: 'image_only',
		icon: 'fa-image',
		title: 'A photo of the menu page',
		desc: 'No dish cards at all — just the photo. Use this when you do not want to type every dish in.'
	},
	{
		id: 'image_and_products',
		icon: 'fa-layer-group',
		title: 'Photo first, then the dish list',
		desc: 'Shows the photo on top, dish cards underneath.'
	}
];

function NumberedStep({ n, title, subtitle }) {
	return (
		<div className="flex items-start gap-2.5">
			<span className="mt-0.5 h-6 w-6 shrink-0 rounded-md bg-gold/15 border border-gold/30 text-gold text-xs flex items-center justify-center font-bold">{n}</span>
			<div className="min-w-0">
				<p className="text-sm text-off-white font-semibold leading-tight">{title}</p>
				{subtitle ? <p className="text-xs text-muted-gray mt-0.5">{subtitle}</p> : null}
			</div>
		</div>
	);
}

// Bulk optimiser for photos uploaded before automatic compression existed.
function PhotoOptimizer({ showToast, onFinished }) {
	const [items, setItems] = React.useState(null);
	const [scanning, setScanning] = React.useState(false);
	const [running, setRunning] = React.useState(false);
	const [progress, setProgress] = React.useState({ done: 0, total: 0 });
	const [results, setResults] = React.useState({});
	const cancelRef = React.useRef(false);

	const oversized = React.useMemo(() => (items || []).filter(i => i.needsWork && !i.error), [items]);
	const unreadable = React.useMemo(() => (items || []).filter(i => i.error), [items]);
	const oversizedBytes = oversized.reduce((s, i) => s + i.size, 0);
	const totalBytes = (items || []).reduce((s, i) => s + i.size, 0);

	const finished = Object.values(results);
	const savedBefore = finished.reduce((s, r) => s + (r.before || 0), 0);
	const savedAfter = finished.reduce((s, r) => s + (r.after || 0), 0);

	async function handleScan() {
		setScanning(true);
		setResults({});
		try {
			const found = await scanStorageImages();
			setItems(found);
			const big = found.filter(i => i.needsWork && !i.error);
			showToast(
				big.length
					? `${big.length} photo${big.length === 1 ? '' : 's'} can be made faster`
					: 'All your photos are already fast — nothing to do',
				'success'
			);
		} catch (e) {
			console.error('Photo scan failed:', e);
			showToast('Could not check your photos. Please try again.', 'error');
		} finally {
			setScanning(false);
		}
	}

	async function handleRunAll() {
		if (!oversized.length) return;
		cancelRef.current = false;
		setRunning(true);
		setProgress({ done: 0, total: oversized.length });

		let ok = 0;
		let failed = 0;
		for (let i = 0; i < oversized.length; i++) {
			if (cancelRef.current) break;
			const item = oversized[i];
			try {
				// eslint-disable-next-line no-await-in-loop
				const res = await optimizeExistingImage(item);
				setResults(prev => ({ ...prev, [item.url]: res }));
				if (!res.skipped) ok++;
			} catch (e) {
				console.error('Optimise failed for', item.path, e);
				failed++;
				setResults(prev => ({ ...prev, [item.url]: { error: e.message || 'failed' } }));
			}
			setProgress({ done: i + 1, total: oversized.length });
		}

		setRunning(false);
		clearAllCaches();
		if (onFinished) onFinished();
		if (ok) showToast(`${ok} photo${ok === 1 ? '' : 's'} made faster${failed ? `, ${failed} failed` : ''}`, failed ? 'error' : 'success');
		else if (failed) showToast('Could not optimise the photos. Please try again.', 'error');
		else showToast('Nothing needed changing', 'success');
	}

	return (
		<section className="lg:col-span-2 bg-marble-black/80 border border-gold/30 rounded-xl p-4 sm:p-6">
			<SectionHeading
				icon="fa-bolt"
				title="Speed up photos"
				subtitle="Makes photos you uploaded in the past smaller, so the website loads faster for customers."
				badge={items ? `${(items || []).length} photos` : null}
			/>

			<div className="rounded-lg bg-black/40 border border-gold/15 p-4 text-sm text-off-white/85 leading-relaxed">
				<p>
					Photos you upload <span className="text-gold">from now on</span> are shrunk automatically.
					This button fixes the <span className="text-gold">older</span> ones that are still very large.
				</p>
				<p className="mt-2 text-muted-gray text-xs">
					Your original photos are kept as a backup — nothing is deleted, and the pictures look the same.
				</p>
			</div>

			<div className="mt-4 flex flex-wrap gap-3">
				<button
					type="button"
					onClick={handleScan}
					disabled={scanning || running}
					className="admin-tap px-4 py-2.5 rounded-lg bg-gold text-black font-semibold text-sm hover:bg-deep-gold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
				>
					<i className={`fas ${scanning ? 'fa-spinner fa-spin' : 'fa-magnifying-glass'} mr-2`}></i>
					{scanning ? 'Checking…' : items ? 'Check again' : '1. Check my photos'}
				</button>

				{oversized.length > 0 && (
					<button
						type="button"
						onClick={handleRunAll}
						disabled={running || scanning}
						className="admin-tap px-4 py-2.5 rounded-lg bg-green-600 text-white font-semibold text-sm hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
					>
						<i className={`fas ${running ? 'fa-spinner fa-spin' : 'fa-bolt'} mr-2`}></i>
						{running ? `Working… ${progress.done}/${progress.total}` : `2. Make ${oversized.length} photo${oversized.length === 1 ? '' : 's'} faster`}
					</button>
				)}

				{running && (
					<button
						type="button"
						onClick={() => { cancelRef.current = true; }}
						className="admin-tap px-4 py-2.5 rounded-lg border border-gold/30 text-off-white text-sm hover:border-gold/60"
					>
						Stop
					</button>
				)}
			</div>

			{running && (
				<div className="mt-4 h-2 rounded-full bg-black/60 overflow-hidden">
					<div
						className="h-full bg-gold transition-all duration-300"
						style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
					/>
				</div>
			)}

			{items && (
				<div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
					<div className="rounded-lg bg-black/40 border border-gold/15 p-3">
						<p className="text-xs text-muted-gray">All photos</p>
						<p className="text-lg text-off-white font-semibold">{items.length} · {formatBytes(totalBytes)}</p>
					</div>
					<div className="rounded-lg bg-black/40 border border-gold/15 p-3">
						<p className="text-xs text-muted-gray">Too big / slow</p>
						<p className={`text-lg font-semibold ${oversized.length ? 'text-amber-400' : 'text-green-400'}`}>
							{oversized.length} · {formatBytes(oversizedBytes)}
						</p>
					</div>
					<div className="rounded-lg bg-black/40 border border-gold/15 p-3">
						<p className="text-xs text-muted-gray">Saved so far</p>
						<p className="text-lg text-green-400 font-semibold">
							{savedBefore > savedAfter ? `${formatBytes(savedBefore - savedAfter)}` : '—'}
						</p>
					</div>
				</div>
			)}

			{items && oversized.length === 0 && !unreadable.length && (
				<p className="mt-4 text-sm text-green-400">
					<i className="fas fa-circle-check mr-2"></i>
					Every photo is already a good size. Nothing to do.
				</p>
			)}

			{unreadable.length > 0 && (
				<p className="mt-4 text-sm text-amber-400">
					<i className="fas fa-triangle-exclamation mr-2"></i>
					{unreadable.length} photo{unreadable.length === 1 ? '' : 's'} could not be read and will be left alone.
				</p>
			)}

			{oversized.length > 0 && (
				<div className="admin-scroll mt-4 max-h-80 overflow-y-auto rounded-lg border border-gold/15 divide-y divide-gold/10">
					{oversized.map(item => {
						const r = results[item.url];
						return (
							<div key={item.url} className="flex items-center gap-3 p-3 bg-black/30">
								<img src={item.url} alt="" loading="lazy" className="h-12 w-12 rounded object-cover border border-gold/20 shrink-0" />
								<div className="min-w-0 flex-1">
									<p className="text-sm text-off-white truncate">{item.refs[0]?.label || item.path}</p>
									<p className="text-xs text-muted-gray truncate">
										{formatBytes(item.size)}
										{item.refs.length > 1 ? ` · used in ${item.refs.length} places` : ''}
									</p>
								</div>
								<div className="shrink-0 text-right text-xs">
									{!r && <span className="text-muted-gray">waiting</span>}
									{r && r.error && <span className="text-red-400">failed</span>}
									{r && !r.error && r.skipped && <span className="text-muted-gray">no change</span>}
									{r && !r.error && !r.skipped && (
										<span className="text-green-400 font-semibold">
											{formatBytes(r.before)} → {formatBytes(r.after)}
										</span>
									)}
								</div>
							</div>
						);
					})}
				</div>
			)}
		</section>
	);
}

const ADMIN_TABS = [
	{ id: 'categories', label: 'Categories', shortLabel: 'Categories', icon: 'fa-folder-tree' },
	{ id: 'dishes', label: 'Dishes', shortLabel: 'Dishes', icon: 'fa-utensils' },
	{ id: 'photos', label: 'Speed up photos', shortLabel: 'Photos', icon: 'fa-bolt' },
	{ id: 'settings', label: 'Settings', shortLabel: 'Settings', icon: 'fa-sliders' },
	{ id: 'import', label: 'Import / Danger zone', shortLabel: 'Import', icon: 'fa-database' }
];

export default function Admin() {
	const { user } = useAuth();
	const { showToast, ToastComponent } = useToast();
	const [refreshKey, setRefreshKey] = React.useState(0);
	const categories = useCategories(refreshKey);
	const products = useProducts(refreshKey);
	const footerSettings = useFooterSettings(refreshKey);
	const appSettings = useAppSettings(refreshKey);
	const [adminLang, setAdminLang] = React.useState('ro');
	const categoryTree = React.useMemo(() => buildCategoryTree(categories, products), [categories, products]);
	const pathOptions = React.useMemo(() => {
		const byId = new Map(buildPathMap(categories, adminLang).map(o => [o.id, o]));
		// Tree order + depth so every dropdown can show the hierarchy by indentation.
		return categoryTree.map(row => {
			const info = byId.get(row.cat.id);
			return { id: row.cat.id, label: info?.label || '', name: info?.name || '', depth: row.depth };
		});
	}, [categories, categoryTree, adminLang]);
	const [listFilterCategoryId, setListFilterCategoryId] = React.useState('');
	const [listCategoryKind, setListCategoryKind] = React.useState('all');
	const [showOrphansOnly, setShowOrphansOnly] = React.useState(false);
	const [listProductSearch, setListProductSearch] = React.useState('');

	const listFilterCategoryIdSet = React.useMemo(() => {
		if (!listFilterCategoryId) return null;
		const childrenByParent = new Map();
		for (const c of categories) {
			const pid = c.parentId || null;
			if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
			childrenByParent.get(pid).push(c.id);
		}

		const visited = new Set();
		const queue = [listFilterCategoryId];
		while (queue.length) {
			const id = queue.shift();
			if (!id || visited.has(id)) continue;
			visited.add(id);
			const kids = childrenByParent.get(id) || [];
			for (const kid of kids) queue.push(kid);
		}
		return visited;
	}, [categories, listFilterCategoryId]);

	const categoryRows = React.useMemo(() => {
		return categoryTree.filter(row => {
			if (listFilterCategoryIdSet && !listFilterCategoryIdSet.has(row.cat.id)) return false;
			if (listCategoryKind === 'main' && row.depth !== 0) return false;
			if (listCategoryKind === 'sub' && row.depth === 0) return false;
			return true;
		});
	}, [categoryTree, listFilterCategoryIdSet, listCategoryKind]);

	const categoryCounts = React.useMemo(() => ({
		main: categoryTree.filter(r => r.depth === 0).length,
		sub: categoryTree.filter(r => r.depth > 0).length
	}), [categoryTree]);

	// Dishes whose category was deleted (or never set). They render nowhere on the
	// public site, so the admin needs a way to spot and re-assign them.
	const orphanProducts = React.useMemo(() => {
		const ids = new Set(categories.map(c => c.id));
		return products.filter(p => !p.categoryId || !ids.has(p.categoryId));
	}, [products, categories]);

	const filteredProducts = React.useMemo(() => {
		if (showOrphansOnly) return orphanProducts;
		if (!listFilterCategoryIdSet) return products;
		return products.filter(p => p.categoryId && listFilterCategoryIdSet.has(p.categoryId));
	}, [products, listFilterCategoryIdSet, showOrphansOnly, orphanProducts]);

	const searchedProducts = React.useMemo(() => {
		const q = (listProductSearch || '').trim().toLowerCase();
		if (!q) return filteredProducts;
		const toText = (v) => (v == null ? '' : String(v));
		return filteredProducts.filter((p) => {
			const parts = [];
			if (p?.name && typeof p.name === 'object' && p.name !== null) parts.push(p.name.en, p.name.ro, p.name.ar);
			if (p?.name && typeof p.name === 'string') parts.push(p.name);
			parts.push(p?.name_ro);
			const hay = toText(parts.filter(Boolean).join(' ')).toLowerCase();
			return hay.includes(q);
		});
	}, [filteredProducts, listProductSearch]);

	// Helper function to safely get name
	const getName = React.useCallback((item) => {
		if (!item) return '';
		if (typeof item.name === 'object' && item.name !== null) {
			return item.name[adminLang] || item.name.en || item.name.ro || item.name.ar || '';
		}
		return item.name || '';
	}, [adminLang]);

	// Category form state
	const [editingCat, setEditingCat] = React.useState(null);
	const [catNames, setCatNames] = React.useState({ en: '', ro: '', ar: '' });
	const [parentId, setParentId] = React.useState('');
	const [catImage, setCatImage] = React.useState(null);
	const [catImageUrl, setCatImageUrl] = React.useState('');
	const [catUseDefaultImage, setCatUseDefaultImage] = React.useState(true);
	const [catMenuPageMode, setCatMenuPageMode] = React.useState('products_only');
	const [catMenuPageImages, setCatMenuPageImages] = React.useState([]);
	const [catMenuPageNewFiles, setCatMenuPageNewFiles] = React.useState([]);
	const [catUseDefaultMenuPageImages, setCatUseDefaultMenuPageImages] = React.useState(true);
	const [catMenuPageInheritToChildren, setCatMenuPageInheritToChildren] = React.useState(false);
	const [catMenuPageChildrenMode, setCatMenuPageChildrenMode] = React.useState('image_only');

	// Product form state
	const [editingProd, setEditingProd] = React.useState(null);
	const [prodNames, setProdNames] = React.useState({ en: '', ro: '', ar: '' });
	const [prodDescs, setProdDescs] = React.useState({ en: '', ro: '', ar: '' });
	const [prodPrice, setProdPrice] = React.useState('');
	const [prodCatId, setProdCatId] = React.useState('');
	const [prodImage, setProdImage] = React.useState(null);
	const [prodImageUrl, setProdImageUrl] = React.useState('');

	// Which panel of the admin is visible. Splitting this up keeps the page from
	// being one very long confusing scroll.
	const [activeTab, setActiveTab] = React.useState('categories');

	const categoriesById = React.useMemo(() => new Map(categories.map(c => [c.id, c])), [categories]);

	const uploadStatsRef = React.useRef([]);

	// The built-in image for the category currently being edited, based on the name
	// typed in the form and the selected parent (so it updates live).
	const catDefaultImage = React.useMemo(() => {
		const draft = {
			id: editingCat?.id,
			name: { en: catNames.en, ro: catNames.ro, ar: catNames.ar },
			name_ro: catNames.ro,
			parentId: parentId || null
		};
		return getDefaultCategoryImage(draft, parentId ? categoriesById.get(parentId) : null);
	}, [catNames, parentId, editingCat, categoriesById]);

	const catDefaultMenuPageImages = React.useMemo(() => {
		const draft = { name: { en: catNames.en, ro: catNames.ro, ar: catNames.ar }, name_ro: catNames.ro };
		return getDefaultMenuPageImages(draft) || [];
	}, [catNames]);

	const parentOptions = React.useMemo(() => {
		if (!editingCat) return pathOptions;
		const blocked = collectDescendantIds(categories, editingCat.id);
		blocked.add(editingCat.id);
		return pathOptions.filter(o => !blocked.has(o.id));
	}, [pathOptions, categories, editingCat]);

	// Live context for the category form so the admin can see what the choices affect.
	const editingCatRow = React.useMemo(
		() => (editingCat ? categoryTree.find(r => r.cat.id === editingCat.id) : null),
		[categoryTree, editingCat]
	);
	const catSubCount = editingCatRow?.childCount || 0;
	const catDishCount = editingCatRow?.dishCount || 0;

	// Will a full-page photo actually be shown after saving? Covers a pending upload,
	// already-saved images, and the built-in default pages.
	const catHasMenuPagePhoto =
		(Array.isArray(catMenuPageNewFiles) && catMenuPageNewFiles.length > 0) ||
		(Array.isArray(catMenuPageImages) && catMenuPageImages.length > 0) ||
		(catDefaultMenuPageImages.length > 0 && catUseDefaultMenuPageImages);

	const parentIsMissing = !!parentId && !categoriesById.has(parentId);

	// A subcategory can borrow its parent's menu photo. Probe with a bare record so the
	// option shows up even when this category currently has a photo of its own.
	const inheritedMenuPage = React.useMemo(() => {
		if (!editingCat) return null;
		return resolveInheritedMenuPage({ id: editingCat.id, parentId: parentId || null }, categoriesById);
	}, [editingCat, parentId, categoriesById]);

	const tapBehaviorOptions = React.useMemo(() => {
		if (!inheritedMenuPage) return TAP_BEHAVIOR_OPTIONS;
		return [
			...TAP_BEHAVIOR_OPTIONS,
			{
				id: 'inherit',
				icon: 'fa-link',
				title: `The photo from ${getName(inheritedMenuPage.parent)}`,
				desc: 'Use the same menu photo as the category above. Change it there and this one follows.'
			}
		];
	}, [inheritedMenuPage, getName]);

	// 'inherit' is only valid while a lending parent is selected. Changing the parent to
	// one without a menu photo must drop back to a real mode, not leave a dead value.
	React.useEffect(() => {
		if (catMenuPageMode === 'inherit' && !inheritedMenuPage) setCatMenuPageMode('products_only');
	}, [catMenuPageMode, inheritedMenuPage]);

	function resetCategoryForm() {
		setEditingCat(null);
		setCatNames({ en: '', ro: '', ar: '' });
		setParentId('');
		setCatImage(null);
		setCatImageUrl('');
		setCatUseDefaultImage(true);
		setCatMenuPageMode('products_only');
		setCatMenuPageImages([]);
		setCatMenuPageNewFiles([]);
		setCatUseDefaultMenuPageImages(true);
		setCatMenuPageInheritToChildren(false);
		setCatMenuPageChildrenMode('image_only');
	}

	function resetProductForm() {
		setEditingProd(null);
		setProdNames({ en: '', ro: '', ar: '' });
		setProdDescs({ en: '', ro: '', ar: '' });
		setProdPrice('');
		setProdCatId('');
		setProdImage(null);
		setProdImageUrl('');
	}

	// Footer settings state
	const [footerAddress, setFooterAddress] = React.useState('');
	const [footerPhone, setFooterPhone] = React.useState('');
	const [footerEmail, setFooterEmail] = React.useState('');
	const [hoursMonThu, setHoursMonThu] = React.useState('');
	const [hoursFriSat, setHoursFriSat] = React.useState('');
	const [hoursSun, setHoursSun] = React.useState('');
	const [instagramUrl, setInstagramUrl] = React.useState('');
	const [facebookUrl, setFacebookUrl] = React.useState('');
	const [tiktokUrl, setTiktokUrl] = React.useState('');

	// App settings state
	const [showProductPlaceholderImage, setShowProductPlaceholderImage] = React.useState(true);

	// Bulk import state
	const [importing, setImporting] = React.useState(false);
	const [importProgress, setImportProgress] = React.useState('');

	// Load footer settings into form when available
	React.useEffect(() => {
		if (footerSettings) {
			setFooterAddress(footerSettings.address || '');
			setFooterPhone(footerSettings.phone || '');
			setFooterEmail(footerSettings.email || '');
			setHoursMonThu(footerSettings.hoursMonThu || '');
			setHoursFriSat(footerSettings.hoursFriSat || '');
			setHoursSun(footerSettings.hoursSun || '');
			setInstagramUrl(footerSettings.instagramUrl || '');
			setFacebookUrl(footerSettings.facebookUrl || '');
			setTiktokUrl(footerSettings.tiktokUrl || '');
		}
	}, [footerSettings]);

	React.useEffect(() => {
		if (appSettings) {
			setShowProductPlaceholderImage(appSettings.showProductPlaceholderImage !== false);
		}
	}, [appSettings]);

	async function handleSaveAppSettings(e) {
		e.preventDefault();
		try {
			await setDoc(doc(db, 'settings', 'app'), {
				showProductPlaceholderImage: !!showProductPlaceholderImage,
				updatedAt: Date.now()
			}, { merge: true });
			showToast('App settings saved successfully!', 'success');
			clearAllCaches();
			setRefreshKey(prev => prev + 1);
		} catch (error) {
			console.error('Error saving app settings:', error);
			showToast('Failed to save app settings. Please try again.', 'error');
		}
	}

	async function uploadToStorage(file, folder) {
		const profile = UPLOAD_PROFILES[folder] || UPLOAD_PROFILES['category-images'];
		const opt = await optimizeImage(file, profile);

		// Surface what the customer will actually download, so a silent
		// pass-through (the old bug) is immediately visible instead of invisible.
		if (opt.converted) {
			uploadStatsRef.current.push({ before: opt.originalSize, after: opt.optimizedSize });
		} else {
			uploadStatsRef.current.push({ before: opt.originalSize, after: opt.originalSize, skipped: opt.skipped });
		}

		const safeName = sanitizeFilename(opt.file?.name || file?.name);
		const fileRef = ref(storage, `${folder}/${Date.now()}_${safeName}`);
		const res = await uploadBytes(fileRef, opt.file, {
			contentType: opt.file?.type || file?.type || undefined,
			cacheControl: 'public,max-age=31536000,immutable'
		});
		return await getDownloadURL(res.ref);
	}

	// Collects per-save optimisation results so one toast can summarise them all.
	function beginUploadBatch() {
		uploadStatsRef.current = [];
	}

	function reportUploadBatch() {
		const stats = uploadStatsRef.current;
		uploadStatsRef.current = [];
		if (!stats.length) return;
		const before = stats.reduce((s, x) => s + x.before, 0);
		const after = stats.reduce((s, x) => s + x.after, 0);
		if (before <= 0 || after >= before) return;
		const pct = Math.round((1 - after / before) * 100);
		showToast(`Images optimised: ${formatBytes(before)} → ${formatBytes(after)} (${pct}% smaller)`, 'success');
	}

	async function handleAddCategory(e) {
		e.preventDefault();
		
		// Validate all languages are filled
		if (!catNames.en.trim() || !catNames.ro.trim() || !catNames.ar.trim()) {
			showToast('Please fill in category name for all languages (EN, RO, AR)', 'error');
			return;
		}

		if (parentIsMissing) {
			showToast('This category is nested inside a deleted category. Pick a new parent, or make it a main category.', 'error');
			return;
		}
		
		try {
			beginUploadBatch();
			// Always write imageUrl (not conditionally) so "Remove" can actually clear it.
			let imageUrl = catImageUrl || '';
			if (catImage) imageUrl = await uploadToStorage(catImage, 'category-images');
			// Uploading a picture means the admin wants it instead of the built-in default.
			const useDefaultImage = catImage ? false : !!catUseDefaultImage;

			const isInheritingMenuPage = catMenuPageMode === 'inherit';
			let menuPageImages = Array.isArray(catMenuPageImages) ? [...catMenuPageImages] : [];
			if (!isInheritingMenuPage && Array.isArray(catMenuPageNewFiles) && catMenuPageNewFiles.length > 0) {
				for (const f of catMenuPageNewFiles) {
					// eslint-disable-next-line no-await-in-loop
					const url = await uploadToStorage(f, 'menu-page-images');
					if (url) menuPageImages.push(url);
				}
			}

			const payload = {
				name: {
					en: catNames.en.trim(),
					ro: catNames.ro.trim(),
					ar: catNames.ar.trim()
				},
				name_ro: catNames.ro.trim(),
				parentId: parentId || null,
				imageUrl,
				useDefaultImage,
				// 'inherit' is a form-only value: clearing both fields is what makes the
				// site fall back to the parent's menu photo.
				menuPageMode: isInheritingMenuPage ? '' : (catMenuPageMode || 'products_only'),
				menuPageImages: isInheritingMenuPage ? [] : menuPageImages,
				useDefaultMenuPageImages: !!catUseDefaultMenuPageImages,
				menuPageInheritToChildren: !!catMenuPageInheritToChildren,
				menuPageChildrenMode: catMenuPageChildrenMode || 'image_only'
			};

			if (editingCat) {
				await updateDoc(doc(db, 'categories', editingCat.id), payload);
				showToast('Category updated successfully!', 'success');
			} else {
				await addDoc(collection(db, 'categories'), { ...payload, createdAt: Date.now() });
				showToast('Category added successfully!', 'success');
			}

			resetCategoryForm();
			e.target.reset();
			clearAllCaches();
			reportUploadBatch();
			setRefreshKey(prev => prev + 1);
		} catch (error) {
			console.error('Error saving category:', error);
			showToast('Failed to save category. Please try again.', 'error');
		}
	}

	async function handleDeleteCategory(id) {
		// Count the whole subtree, not just direct children: deleting a parent
		// silently strands every level below it (that is how orphan dishes appear).
		const descendants = collectDescendantIds(categories, id);
		const affectedCategoryIds = new Set([id, ...descendants]);
		const productCount = products.filter(p => p.categoryId && affectedCategoryIds.has(p.categoryId)).length;
		const warnings = [];
		if (descendants.size) warnings.push(`${descendants.size} subcategor${descendants.size === 1 ? 'y' : 'ies'}`);
		if (productCount) warnings.push(`${productCount} dish${productCount === 1 ? '' : 'es'}`);
		const detail = warnings.length
			? `\n\nWarning: ${warnings.join(' and ')} sit under this category. They are NOT deleted, but they stop showing on the site until you move them somewhere else.`
			: '';
		if (!window.confirm(`Delete "${getName(categoriesById.get(id)) || 'this category'}"?${detail}`)) return;
		try {
			await deleteDoc(doc(db, 'categories', id));
			if (editingCat?.id === id) resetCategoryForm();
			clearAllCaches();
			setRefreshKey(prev => prev + 1);
			showToast('Category deleted successfully!', 'success');
		} catch (error) {
			console.error('Error deleting category:', error);
			showToast('Failed to delete category. Please try again.', 'error');
		}
	}

	function handleEditCategory(cat) {
		setEditingCat(cat);
		setCatImage(null);
		setCatImageUrl(cat?.imageUrl || '');
		setCatUseDefaultImage(cat?.useDefaultImage !== false);
		// A subcategory borrowing its parent's photo must open on 'inherit', otherwise
		// pressing Update would write an explicit mode and permanently break inheritance.
		const inherited = resolveInheritedMenuPage(cat, categoriesById);
		setCatMenuPageMode(inherited ? 'inherit' : effectiveMenuPageMode(cat));
		setCatMenuPageImages(Array.isArray(cat?.menuPageImages) ? cat.menuPageImages.filter(Boolean) : []);
		setCatMenuPageNewFiles([]);
		setCatUseDefaultMenuPageImages(cat?.useDefaultMenuPageImages !== false);
		setCatMenuPageInheritToChildren(!!cat?.menuPageInheritToChildren);
		setCatMenuPageChildrenMode(cat?.menuPageChildrenMode || 'image_only');
		if (typeof cat.name === 'object' && cat.name !== null) {
			setCatNames({
				en: cat.name.en || '',
				ro: cat.name.ro || '',
				ar: cat.name.ar || ''
			});
		} else {
			setCatNames({ en: '', ro: cat.name || '', ar: '' });
		}
		setParentId(cat.parentId || '');
		setActiveTab('categories');
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	async function handleAddProduct(e) {
		e.preventDefault();
		
		// Validate all languages are filled
		if (!prodNames.en.trim() || !prodNames.ro.trim() || !prodNames.ar.trim()) {
			showToast('Please fill in product name for all languages (EN, RO, AR)', 'error');
			return;
		}
		if (!prodDescs.en.trim() || !prodDescs.ro.trim() || !prodDescs.ar.trim()) {
			showToast('Please fill in product description for all languages (EN, RO, AR)', 'error');
			return;
		}
		
		try {
			beginUploadBatch();
			// Always write imageUrl so removing a dish photo actually sticks.
			let imageUrl = prodImageUrl || '';
			if (prodImage) imageUrl = await uploadToStorage(prodImage, 'product-images');

			const payload = {
				name: {
					en: prodNames.en.trim(),
					ro: prodNames.ro.trim(),
					ar: prodNames.ar.trim()
				},
				name_ro: prodNames.ro.trim(),
				description: {
					en: prodDescs.en.trim(),
					ro: prodDescs.ro.trim(),
					ar: prodDescs.ar.trim()
				},
				price: parseFloat(prodPrice),
				categoryId: prodCatId || null,
				imageUrl
			};

			if (editingProd) {
				await updateDoc(doc(db, 'products', editingProd.id), payload);
				showToast('Product updated successfully!', 'success');
			} else {
				await addDoc(collection(db, 'products'), { ...payload, createdAt: Date.now() });
				showToast('Product added successfully!', 'success');
			}

			resetProductForm();
			e.target.reset();
			clearAllCaches();
			reportUploadBatch();
			setRefreshKey(prev => prev + 1);
		} catch (error) {
			console.error('Error saving product:', error);
			showToast('Failed to save product. Please try again.', 'error');
		}
	}

async function handleSaveFooter(e) {
	e.preventDefault();
	try {
		await setDoc(doc(db, 'settings', 'footer'), {
			address: footerAddress.trim(),
			phone: footerPhone.trim(),
			email: footerEmail.trim(),
			hoursMonThu: hoursMonThu.trim(),
			hoursFriSat: hoursFriSat.trim(),
			hoursSun: hoursSun.trim(),
			instagramUrl: instagramUrl.trim(),
			facebookUrl: facebookUrl.trim(),
			tiktokUrl: tiktokUrl.trim(),
			updatedAt: Date.now()
		});
		showToast('Footer settings saved successfully!', 'success');
		clearAllCaches();
		setRefreshKey(prev => prev + 1);
	} catch (error) {
		console.error('Error saving footer:', error);
		showToast('Failed to save footer settings. Please try again.', 'error');
	}
}

	async function handleDeleteProduct(id) {
		if (!window.confirm('Delete this product?')) return;
		try {
			await deleteDoc(doc(db, 'products', id));
			if (editingProd?.id === id) resetProductForm();
			clearAllCaches();
			setRefreshKey(prev => prev + 1);
			showToast('Product deleted successfully!', 'success');
		} catch (error) {
			console.error('Error deleting product:', error);
			showToast('Failed to delete product. Please try again.', 'error');
		}
	}

	function handleEditProduct(prod) {
		setEditingProd(prod);
		setProdImage(null);
		setProdImageUrl(prod?.imageUrl || '');
		if (typeof prod.name === 'object' && prod.name !== null) {
			setProdNames({ en: prod.name.en || '', ro: prod.name.ro || '', ar: prod.name.ar || '' });
		} else {
			setProdNames({ en: '', ro: prod.name || '', ar: '' });
		}
		if (typeof prod.description === 'object' && prod.description !== null) {
			setProdDescs({ en: prod.description.en || '', ro: prod.description.ro || '', ar: prod.description.ar || '' });
		} else {
			setProdDescs({ en: prod.description || '', ro: '', ar: '' });
		}
		setProdPrice(prod.price == null ? '' : String(prod.price));
		setProdCatId(prod.categoryId || '');
		setActiveTab('dishes');
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	async function handleBulkImport() {
		if (!window.confirm('This will import all menu items from the menu files. Continue?')) return;
		
		setImporting(true);
		setImportProgress('Loading menu files...');
		
		try {
			// Fetch the menu files
			const [enResponse, roResponse, arResponse] = await Promise.all([
				fetch('/Lotus_Menu_English.txt'),
				fetch('/Lotus_Menu_Romanian.txt'),
				fetch('/Lotus_Menu_Arabic.txt')
			]);
			
			const [enText, roText, arText] = await Promise.all([
				enResponse.text(),
				roResponse.text(),
				arResponse.text()
			]);
			
			setImportProgress('Parsing menu data...');
			const { categories, products, issues } = parseMenuData(enText, roText, arText);
			if (issues && issues.length > 0) {
				console.error('Menu parse issues:', issues);
				setImportProgress(`Parsing failed. Found ${issues.length} mismatch(es). Not importing to avoid wrong translations.\n\nFirst issues:\n- ${issues.slice(0, 10).join('\n- ')}`);
				showToast(`Parsing failed (${issues.length} mismatch(es)). Not imported. Check console.`, 'error');
				return;
			}
			
			setImportProgress(`Found ${categories.length} categories and ${products.length} products. Importing...`);
			
			const results = await importMenuToFirestore(categories, products);
			
			setImportProgress(`Import complete! Added ${results.categoriesAdded} categories and ${results.productsAdded} products.`);
			
			if (results.errors.length > 0) {
				console.error('Import errors:', results.errors);
				showToast(`Import completed with ${results.errors.length} errors. Check console.`, 'error');
			} else {
				showToast(`Successfully imported ${results.categoriesAdded} categories and ${results.productsAdded} products!`, 'success');
			}
			
			setRefreshKey(prev => prev + 1);
		} catch (error) {
			console.error('Bulk import error:', error);
			showToast('Failed to import menu. See console for details.', 'error');
			setImportProgress('Import failed: ' + error.message);
		} finally {
			setImporting(false);
			setTimeout(() => setImportProgress(''), 5000);
		}
	}

	async function handleManualFullMenuImport() {
		if (!window.confirm('This will import the FULL menu using the manual JSON seeds (safe to re-run). Continue?')) return;

		setImporting(true);
		setImportProgress('Starting manual full-menu import...');
		try {
			const datasets = [
				{ label: 'Soups/Breakfast/Salads', data: manualMenuSeed },
				{ label: 'Appetizers', data: manualMenuAppetizers },
				{ label: 'Grill/International/Sides/Pasta', data: manualMenuCatsMore },
				{ label: 'Burgers', data: manualMenuBurgers },
				{ label: 'Desserts/Coffee/Freak shakes/Lemonades', data: manualMenuDessertsDrinks },
				{ label: 'Soft drinks/Cocktails/Mocktails', data: manualMenuSoftDrinksCocktails },
				{ label: 'Sauces', data: manualMenuSauces }
			];

			let totalCategories = 0;
			let totalProducts = 0;
			const allErrors = [];

			for (const ds of datasets) {
				setImportProgress(`Importing ${ds.label}...`);
				// eslint-disable-next-line no-await-in-loop
				const results = await importManualMenuJsonToFirestore(ds.data);
				totalCategories += results.categoriesAdded || 0;
				totalProducts += results.productsAdded || 0;
				if (results.errors?.length) {
					allErrors.push(...results.errors.map(e => `[${ds.label}] ${e}`));
				}
			}

			setImportProgress(`Manual full-menu import complete! Upserted ${totalCategories} categories and ${totalProducts} items.`);
			if (allErrors.length > 0) {
				console.error('Manual full-menu import errors:', allErrors);
				showToast(`Manual full-menu import completed with ${allErrors.length} errors. Check console.`, 'error');
			} else {
				showToast(`Manual full-menu import completed successfully!`, 'success');
			}
			setRefreshKey(prev => prev + 1);
		} catch (error) {
			console.error('Manual full-menu import error:', error);
			showToast('Failed to import manual full menu. See console for details.', 'error');
			setImportProgress('Import failed: ' + error.message);
		} finally {
			setImporting(false);
			setTimeout(() => setImportProgress(''), 5000);
		}
	}

	async function handleClearAllData() {
		if (!window.confirm('⚠️ WARNING: This will delete ALL categories and products! Are you absolutely sure?')) return;
		if (!window.confirm('This action cannot be undone! Type confirmation needed.')) return;
		
		try {
			await clearAllMenuData();
			showToast('All menu data cleared successfully!', 'success');
			resetCategoryForm();
			resetProductForm();
			clearAllCaches();
			setRefreshKey(prev => prev + 1);
		} catch (error) {
			console.error('Clear data error:', error);
			showToast('Failed to clear menu data.', 'error');
		}
	}

	return (
		<div className="min-h-screen marble-bg marble-overlay text-off-white">
	<header className="sticky top-0 z-30 bg-marble-black/95 backdrop-blur-md border-b border-gold/20">
		<div className="max-w-7xl mx-auto px-4 sm:px-6">
			<div className="flex items-center justify-between gap-2 sm:gap-4 py-3">
				<div className="flex items-center gap-3 min-w-0">
					<img src="/lotus-logo.png" alt="Lotus" className="h-9 sm:h-11 w-auto shrink-0 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => window.location.href = '/'} />
					<div className="hidden sm:block min-w-0">
						<h1 className="font-cinzel text-lg text-gold leading-tight">Admin Panel</h1>
						<p className="text-xs text-muted-gray truncate">{user?.email}</p>
					</div>
				</div>
				<div className="flex items-center gap-2 sm:gap-3 shrink-0">
					<div className="flex items-center gap-1 bg-black/50 border border-gold/25 p-1 rounded-lg">
						{['ro', 'en', 'ar'].map(lng => (
							<button
								key={lng}
								type="button"
								onClick={() => setAdminLang(lng)}
								className={`admin-tap px-3 sm:px-2.5 py-1 rounded text-xs font-semibold transition-colors ${adminLang === lng ? 'bg-gold text-black' : 'text-off-white hover:text-gold'}`}
							>
								{lng.toUpperCase()}
							</button>
						))}
					</div>
					<a
						href="/"
						target="_blank"
						rel="noopener noreferrer"
						className="hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gold/25 text-off-white hover:border-gold/60 hover:text-gold transition-colors text-sm"
					>
						<i className="fas fa-arrow-up-right-from-square"></i>View site
					</a>
					<button onClick={signOutUser} title="Sign out" className="px-3 py-2 bg-gold text-black rounded-lg hover:bg-deep-gold transition-colors text-sm font-semibold whitespace-nowrap">
						<i className="fas fa-right-from-bracket sm:mr-1.5"></i><span className="hidden sm:inline">Sign out</span>
					</button>
				</div>
			</div>
			<nav className="admin-scroll flex gap-2 pb-3 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
				{ADMIN_TABS.map(tab => (
					<button
						key={tab.id}
						type="button"
						onClick={() => setActiveTab(tab.id)}
						className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-2 rounded-lg text-sm border transition-colors ${
							activeTab === tab.id
								? 'bg-gold text-black border-gold font-semibold shadow-lg shadow-gold/20'
								: 'bg-black/40 text-off-white border-gold/20 hover:border-gold/50 hover:text-gold'
						}`}
					>
						<i className={`fas ${tab.icon} mr-2`}></i>
						<span className="sm:hidden">{tab.shortLabel || tab.label}</span>
						<span className="hidden sm:inline">{tab.label}</span>
					</button>
				))}
			</nav>
		</div>
	</header>

			<main className="px-4 sm:px-6 py-6 sm:py-8">
				<div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
				{activeTab === 'categories' ? (
					<section className="bg-marble-black/80 border border-gold/30 rounded-xl p-4 sm:p-6">
						<SectionHeading
							icon={editingCat ? 'fa-pen' : 'fa-plus'}
							title={editingCat ? 'Edit Category' : 'Add Category'}
							subtitle={editingCat ? 'Editing an existing category. Cancel to start a new one.' : 'Create a top-level category or a subcategory.'}
							badge={editingCat ? 'Editing' : null}
						/>
						<div className="mb-5 p-3 rounded-lg bg-black/40 border border-gold/15">
							<p className="text-xs text-muted-gray leading-relaxed">
								<i className="fas fa-circle-info text-gold mr-1.5"></i>
								Each category has <span className="text-off-white">two different pictures</span>:
								the <span className="text-off-white">card image</span> (small tile in the grid, before tapping) and the
								<span className="text-off-white"> menu photo</span> (full page shown after tapping, instead of typing every dish).
								Dishes only ever have one photo.
							</p>
						</div>
						<form onSubmit={handleAddCategory} className="space-y-4">
							<div>
								<LangTabs
									value={adminLang}
									onChange={setAdminLang}
									filled={{ ro: !!catNames.ro.trim(), en: !!catNames.en.trim(), ar: !!catNames.ar.trim() }}
								/>
								<input value={catNames[adminLang]}
									onChange={e=>setCatNames(prev=>({ ...prev, [adminLang]: e.target.value }))}
									placeholder={`Category name (${adminLang.toUpperCase()})`} 
									className="admin-input" required />
							</div>
							<div>
								<label className="block text-xs text-muted-gray mb-1.5">Category type</label>
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
									<button
										type="button"
										onClick={() => setParentId('')}
										className={`text-left p-3 rounded-lg border transition-colors ${!parentId ? 'bg-sky-500/15 border-sky-400/50' : 'bg-black/40 border-gold/15 hover:border-gold/40'}`}
									>
										<span className={`flex items-center gap-2 text-sm font-semibold ${!parentId ? 'text-sky-300' : 'text-off-white'}`}>
											<i className="fas fa-folder"></i>Main category
										</span>
										<span className="block text-[11px] text-muted-gray mt-1">Appears on the menu home screen.</span>
									</button>
									<button
										type="button"
										disabled={parentOptions.length === 0}
										onClick={() => { if (!parentId) setParentId(parentOptions[0]?.id || ''); }}
										className={`text-left p-3 rounded-lg border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${parentId ? 'bg-violet-500/15 border-violet-400/50' : 'bg-black/40 border-gold/15 hover:border-gold/40'}`}
									>
										<span className={`flex items-center gap-2 text-sm font-semibold ${parentId ? 'text-violet-300' : 'text-off-white'}`}>
											<i className="fas fa-diagram-next"></i>Subcategory
										</span>
										<span className="block text-[11px] text-muted-gray mt-1">
											{parentOptions.length === 0 ? 'No category available to nest under.' : 'Appears inside another category.'}
										</span>
									</button>
								</div>

								{parentId ? (
									<div className="mt-2">
										<label className="block text-xs text-muted-gray mb-1.5">Nested inside</label>
										<select value={parentId} onChange={e=>setParentId(e.target.value)} className="admin-input">
											{parentIsMissing ? (
												<option value={parentId} disabled>⚠ Parent was deleted — pick a new one</option>
											) : null}
											{parentOptions.map(o=> (
												<option key={o.id} value={o.id}>{hierarchyOptionLabel(o)}</option>
											))}
										</select>
										{parentIsMissing ? (
											<p className="text-xs text-red-400 mt-1.5">
												<i className="fas fa-triangle-exclamation mr-1"></i>
												The category this was nested inside no longer exists, so this one is hidden from the site. Pick a new parent, or switch it to a main category.
											</p>
										) : null}
									</div>
								) : null}

								<p className="text-xs text-muted-gray mt-2">
									{parentId ? (
										<>
											<i className="fas fa-diagram-next text-violet-300 mr-1"></i>
											Path: <span className="text-off-white">{parentOptions.find(o => o.id === parentId)?.label || '…'} / {catNames[adminLang].trim() || 'New subcategory'}</span>
										</>
									) : (
										<>
											<i className="fas fa-folder text-sky-300 mr-1"></i>
											This is a <span className="text-off-white">main category</span> — it shows directly on the menu screen.
										</>
									)}
								</p>
							</div>

							<div className="space-y-3 pt-3 border-t border-gold/10">
								<NumberedStep
									n="1"
									title="Card image"
									subtitle="The small picture in the menu grid — what people see BEFORE tapping."
								/>
								<ImageField
									label="Card image"
									hint="Shown on the category tile in the menu."
									file={catImage}
									currentUrl={catImageUrl}
									defaultSrc={catDefaultImage}
									useDefault={catUseDefaultImage}
									onFileChange={(f) => setCatImage(f)}
									onRemove={() => {
										if (catImage) { setCatImage(null); return; }
										setCatImageUrl('');
										setCatUseDefaultImage(false);
									}}
									onRestoreDefault={() => { setCatImage(null); setCatUseDefaultImage(true); }}
								/>
							</div>

							<div className="space-y-3 pt-3 border-t border-gold/10">
								<NumberedStep
									n="2"
									title="What opens when someone taps it"
									subtitle="This is where you choose between a dish list and a photo of the printed menu."
								/>

								<div className="space-y-2">
									{tapBehaviorOptions.map(opt => {
										const active = catMenuPageMode === opt.id;
										return (
											<button
												key={opt.id}
												type="button"
												onClick={() => setCatMenuPageMode(opt.id)}
												className={`w-full flex items-start gap-3 text-left px-3 py-2.5 rounded-lg border transition-colors ${active ? 'bg-gold/10 border-gold/50' : 'bg-black/40 border-gold/10 hover:border-gold/35'}`}
											>
												<span className={`mt-0.5 h-8 w-8 shrink-0 rounded-lg flex items-center justify-center border ${active ? 'bg-gold/20 border-gold/40 text-gold' : 'bg-black/50 border-gold/15 text-muted-gray'}`}>
													<i className={`fas ${opt.icon} text-sm`}></i>
												</span>
												<span className="min-w-0">
													<span className={`block text-sm font-semibold ${active ? 'text-gold' : 'text-off-white'}`}>{opt.title}</span>
													<span className="block text-xs text-muted-gray mt-0.5">{opt.desc}</span>
												</span>
												{active ? <i className="fas fa-check text-gold ml-auto mt-1"></i> : null}
											</button>
										);
									})}
								</div>

								{catMenuPageMode === 'inherit' && inheritedMenuPage ? (
									<div className="space-y-3 p-4 rounded-lg bg-black/30 border border-violet-400/30">
										<p className="text-xs text-violet-200">
											<i className="fas fa-link mr-1"></i>
											These photos live on <span className="text-off-white font-semibold">{getName(inheritedMenuPage.parent)}</span>.
											Edit that category to change them, or pick a different option above to give this one its own photo.
										</p>
										<div className="grid grid-cols-2 gap-2">
											{inheritedMenuPage.images.slice(0, 4).map((url, idx) => (
												<img key={`${url}-${idx}`} src={url} alt="Inherited menu page" className="h-20 w-full rounded object-cover border border-gold/10" loading="lazy" decoding="async" />
											))}
										</div>
									</div>
								) : null}

								{catMenuPageMode !== 'products_only' && catMenuPageMode !== 'inherit' ? (
									<div className="space-y-3 p-4 rounded-lg bg-black/30 border border-gold/20">
										<div>
											<p className="text-sm text-off-white font-medium">Menu photo</p>
											<p className="text-xs text-muted-gray mt-0.5">The full-page picture shown after tapping. You can upload more than one for multi-page menus.</p>
										</div>

										{Array.isArray(catMenuPageNewFiles) && catMenuPageNewFiles.length > 0 ? (
											<div className="flex items-center justify-between gap-3 p-2 rounded-lg bg-blue-500/10 border border-blue-400/30">
												<p className="text-xs text-blue-200 truncate">
													<i className="fas fa-upload mr-1"></i>
													{catMenuPageNewFiles.length} new photo{catMenuPageNewFiles.length === 1 ? '' : 's'} ready — press Save to apply.
												</p>
												<button
													type="button"
													onClick={() => setCatMenuPageNewFiles([])}
													className="shrink-0 px-2 py-1 rounded border border-blue-400/40 text-blue-200 hover:bg-blue-500/15 text-xs"
												>
													Cancel
												</button>
											</div>
										) : null}

										{Array.isArray(catMenuPageImages) && catMenuPageImages.length > 0 ? (
											<div className="space-y-2">
												<div className="flex items-center justify-between gap-3">
													<p className="text-xs text-gold">
														<i className="fas fa-image mr-1"></i>Your uploaded photo{catMenuPageImages.length === 1 ? '' : 's'} ({catMenuPageImages.length})
													</p>
													<button
														type="button"
														onClick={() => setCatMenuPageImages([])}
														className="px-2 py-1 bg-red-500/10 text-red-400 border border-red-500/30 rounded hover:bg-red-500/20 text-xs"
													>
														Remove all
													</button>
												</div>
												<div className="grid grid-cols-2 gap-2">
													{catMenuPageImages.map((url, idx) => (
														<div key={`${url}-${idx}`} className="flex items-center gap-2 bg-black/40 border border-gold/10 rounded-lg p-2">
															<img src={url} alt="Menu page" className="h-12 w-12 rounded object-cover border border-gold/10" loading="lazy" decoding="async" />
															<div className="flex-1 min-w-0">
																<p className="text-xs text-off-white/80 truncate">Page {idx + 1}</p>
																<button
																	type="button"
																	onClick={() => setCatMenuPageImages(prev => (Array.isArray(prev) ? prev.filter((_, i) => i !== idx) : []))}
																	className="mt-1 px-2 py-0.5 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 text-xs"
																>
																	Remove
																</button>
															</div>
														</div>
													))}
												</div>
											</div>
										) : catDefaultMenuPageImages.length > 0 ? (
											<div className="space-y-2">
												<div className="flex items-center justify-between gap-3">
													<p className="text-xs text-muted-gray">
														Built-in photo{catDefaultMenuPageImages.length === 1 ? '' : 's'}
														<span className={`ml-2 text-[11px] px-2 py-0.5 rounded-full border ${catUseDefaultMenuPageImages ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30' : 'bg-muted-gray/15 text-muted-gray border-muted-gray/30'}`}>
															{catUseDefaultMenuPageImages ? 'In use' : 'Removed'}
														</span>
													</p>
													<button
														type="button"
														onClick={() => setCatUseDefaultMenuPageImages(v => !v)}
														className={`px-2 py-1 rounded border text-xs ${catUseDefaultMenuPageImages ? 'bg-red-500/10 text-red-400 border-red-500/30 hover:bg-red-500/20' : 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30 hover:bg-emerald-500/25'}`}
													>
														{catUseDefaultMenuPageImages ? 'Remove' : 'Use it again'}
													</button>
												</div>
												<div className="grid grid-cols-2 gap-2">
													{catDefaultMenuPageImages.map((url, idx) => (
														<img key={`${url}-${idx}`} src={url} alt="Default menu page" className={`h-20 w-full rounded object-cover border border-gold/10 ${catUseDefaultMenuPageImages ? '' : 'opacity-30 grayscale'}`} loading="lazy" decoding="async" />
													))}
												</div>
											</div>
										) : null}

										<div>
											<input
												id="menu-page-upload"
												type="file"
												accept="image/*"
												multiple
												onChange={(e) => {
													// The input only ever holds the latest pick, so append instead of
													// replacing, and ignore the empty change a cancelled dialog fires.
													const picked = Array.from(e.target.files || []);
													if (picked.length > 0) setCatMenuPageNewFiles(prev => [...prev, ...picked]);
													e.target.value = '';
												}}
												className="hidden"
											/>
											<label
												htmlFor="menu-page-upload"
												className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold/20 text-gold border border-gold/30 rounded hover:bg-gold/30 text-xs cursor-pointer"
											>
												<i className="fas fa-upload"></i>
												{(Array.isArray(catMenuPageImages) && catMenuPageImages.length > 0) ? 'Add another page' : 'Upload menu photo'}
											</label>
											<p className="text-xs text-muted-gray mt-1.5">
												{(Array.isArray(catMenuPageImages) && catMenuPageImages.length > 0)
													? 'New photos are added as extra pages. Remove the old ones first if you want to replace them.'
													: 'You can select more than one file for a multi-page menu.'}
											</p>
										</div>

										{!catHasMenuPagePhoto ? (
											<p className="text-xs text-red-400">
												<i className="fas fa-triangle-exclamation mr-1"></i>
												No photo yet — upload one, or this category will look empty.
											</p>
										) : null}

										{(catSubCount > 0 || !editingCat) ? (
											<div className="pt-3 border-t border-gold/10 space-y-2">
												<label className="flex items-start gap-2 text-sm text-off-white cursor-pointer">
													<input
														type="checkbox"
														className="admin-check mt-1"
														checked={catMenuPageInheritToChildren}
														onChange={(e) => setCatMenuPageInheritToChildren(e.target.checked)}
													/>
													<span>
														Use this same photo for its subcategories
														<span className="block text-xs text-muted-gray mt-0.5">
															{catSubCount > 0
																? `Applies to all ${catSubCount} subcategor${catSubCount === 1 ? 'y' : 'ies'}. Any subcategory with its own photo keeps its own.`
																: 'Only matters once this category has subcategories.'}
														</span>
													</span>
												</label>
												{catMenuPageInheritToChildren ? (
													<div className="pl-6">
														<label className="block text-xs text-muted-gray mb-1.5">Subcategories should show</label>
														<select
															value={catMenuPageChildrenMode}
															onChange={(e) => setCatMenuPageChildrenMode(e.target.value)}
															className="admin-input"
														>
															<option value="image_only">The photo only</option>
															<option value="image_and_products">Photo first, then dishes</option>
															<option value="products_only">Dishes only</option>
														</select>
													</div>
												) : null}
											</div>
										) : null}
									</div>
								) : null}

								<div className="p-3 rounded-lg bg-black/40 border border-gold/15">
									<p className="text-xs text-muted-gray">
										<i className="fas fa-eye text-gold mr-1"></i>
										Tapping <span className="text-off-white">{catNames[adminLang].trim() || 'this category'}</span> will show{' '}
										{catMenuPageMode === 'inherit' ? (
											<span className="text-off-white">
												the menu photo from {getName(inheritedMenuPage?.parent)}
												{inheritedMenuPage?.mode === 'image_only' ? ' only — no dish cards.' : ', then its dishes.'}
											</span>
										) : catMenuPageMode === 'image_only' ? (
											<span className="text-off-white">the menu photo only — no dish cards.</span>
										) : catMenuPageMode === 'image_and_products' ? (
											<span className="text-off-white">the menu photo, then its {catSubCount > 0 ? `${catSubCount} subcategor${catSubCount === 1 ? 'y' : 'ies'}` : `${catDishCount} dish${catDishCount === 1 ? '' : 'es'}`}.</span>
										) : (
											<span className="text-off-white">
												{catSubCount > 0
													? `its ${catSubCount} subcategor${catSubCount === 1 ? 'y' : 'ies'}.`
													: `its ${catDishCount} dish${catDishCount === 1 ? '' : 'es'}.`}
											</span>
										)}
									</p>
								</div>
							</div>
							<div className="flex gap-2 pt-2 border-t border-gold/10">
								<button type="submit" className="px-4 py-2.5 bg-gold text-black rounded-lg hover:bg-deep-gold transition-colors font-semibold flex-1">
									<i className={`fas ${editingCat ? 'fa-check' : 'fa-plus'} mr-2`}></i>{editingCat ? 'Update' : 'Add'} Category
								</button>
								{editingCat && (
									<button type="button" onClick={resetCategoryForm} className="px-4 py-2.5 bg-muted-gray/20 text-off-white rounded-lg hover:bg-muted-gray/30 transition-colors">Cancel</button>
								)}
							</div>
						</form>
					</section>
				) : null}

				{activeTab === 'dishes' ? (
					<section className="bg-marble-black/80 border border-gold/30 rounded-xl p-4 sm:p-6">
						<SectionHeading
							icon={editingProd ? 'fa-pen' : 'fa-plus'}
							title={editingProd ? 'Edit Dish' : 'Add Dish'}
							subtitle={editingProd ? 'Editing an existing dish. Cancel to start a new one.' : 'Add a dish and assign it to a category.'}
							badge={editingProd ? 'Editing' : null}
						/>
						<form onSubmit={handleAddProduct} className="space-y-4">
							<div className="space-y-2">
								<LangTabs
									value={adminLang}
									onChange={setAdminLang}
									filled={{
										ro: !!prodNames.ro.trim() && !!prodDescs.ro.trim(),
										en: !!prodNames.en.trim() && !!prodDescs.en.trim(),
										ar: !!prodNames.ar.trim() && !!prodDescs.ar.trim()
									}}
								/>
								<input value={prodNames[adminLang]}
									onChange={e=>setProdNames(prev=>({ ...prev, [adminLang]: e.target.value }))}
									placeholder={`Dish name (${adminLang.toUpperCase()})`} 
									className="admin-input" required />
								<textarea value={prodDescs[adminLang]}
									onChange={e=>setProdDescs(prev=>({ ...prev, [adminLang]: e.target.value }))}
									placeholder={`Description (${adminLang.toUpperCase()})`} 
									className="admin-input" rows="3" />
							</div>
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
								<div>
									<label className="block text-xs text-muted-gray mb-1.5">Price (LEI)</label>
									<input type="number" step="0.01" min="0" value={prodPrice} onChange={e=>setProdPrice(e.target.value)} placeholder="0.00" className="admin-input" required />
								</div>
								<div>
									<label className="block text-xs text-muted-gray mb-1.5">Category</label>
									<select value={prodCatId} onChange={e=>setProdCatId(e.target.value)} className="admin-input" required>
										<option value="" disabled>Select category</option>
										{prodCatId && !pathOptions.some(o => o.id === prodCatId) ? (
											<option value={prodCatId} disabled>⚠ Deleted category — pick a new one</option>
										) : null}
										{pathOptions.map(o=> (
											<option key={o.id} value={o.id}>{hierarchyOptionLabel(o)}</option>
										))}
									</select>
									{prodCatId ? (
										pathOptions.some(o => o.id === prodCatId) ? (
											<p className="text-xs text-muted-gray mt-1.5 truncate">
												<i className={`fas ${pathOptions.find(o => o.id === prodCatId)?.depth ? 'fa-diagram-next text-violet-300' : 'fa-folder text-sky-300'} mr-1`}></i>
												{pathOptions.find(o => o.id === prodCatId)?.label}
											</p>
										) : (
											<p className="text-xs text-red-400 mt-1.5">
												<i className="fas fa-triangle-exclamation mr-1"></i>This dish points at a deleted category.
											</p>
										)
									) : null}
								</div>
							</div>
							<ImageField
								label="Dish photo"
								hint="Optional. Dishes only have this one image — there is no full-page photo for a dish."
								file={prodImage}
								currentUrl={prodImageUrl}
								defaultSrc={null}
								useDefault={false}
								onFileChange={(f) => setProdImage(f)}
								onRemove={() => {
									if (prodImage) { setProdImage(null); return; }
									setProdImageUrl('');
								}}
								onRestoreDefault={() => {}}
							/>
							<div className="flex gap-2 pt-2 border-t border-gold/10">
								<button type="submit" className="px-4 py-2.5 bg-gold text-black rounded-lg hover:bg-deep-gold transition-colors font-semibold flex-1">
									<i className={`fas ${editingProd ? 'fa-check' : 'fa-plus'} mr-2`}></i>{editingProd ? 'Update' : 'Add'} Dish
								</button>
								{editingProd && (
									<button type="button" onClick={resetProductForm} className="px-4 py-2.5 bg-muted-gray/20 text-off-white rounded-lg hover:bg-muted-gray/30 transition-colors">Cancel</button>
								)}
							</div>
						</form>
					</section>
				) : null}

				{activeTab === 'import' ? (
				<section className="lg:col-span-2 bg-marble-black/80 border border-gold/30 rounded-xl p-4 sm:p-6">
					<SectionHeading icon="fa-database" title="Bulk Menu Import" subtitle="One-off tools. Be careful — these change the whole menu." />	
					<div className="space-y-4">
						<div className="flex gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
							<i className="fas fa-triangle-exclamation text-amber-400 mt-0.5"></i>
							<p className="text-off-white/90 text-sm">
								These tools rewrite the entire menu. Import reads <span className="text-gold">Lotus_Menu_English.txt</span>, <span className="text-gold">Lotus_Menu_Romanian.txt</span> and <span className="text-gold">Lotus_Menu_Arabic.txt</span> from the <span className="text-gold">public</span> folder.
							</p>
						</div>
						<div className="flex flex-wrap gap-3">
							<button 
								onClick={handleBulkImport}
								disabled={importing}
								className="px-5 py-2.5 bg-gold text-black rounded-lg hover:bg-deep-gold transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
							>
								<i className="fas fa-file-import mr-2"></i>{importing ? 'Importing…' : 'Import Full Menu'}
							</button>
							<button 
								onClick={handleManualFullMenuImport}
								disabled={importing}
								className="px-5 py-2.5 bg-gold/15 text-gold border border-gold/40 rounded-lg hover:bg-gold/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
							>
								<i className="fas fa-code mr-2"></i>Import Full Menu (Manual JSON)
							</button>
							<button 
								onClick={handleClearAllData}
								disabled={importing}
								className="px-5 py-2.5 bg-red-500/15 text-red-400 border border-red-500/40 rounded-lg hover:bg-red-500/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold ml-auto"
							>
								<i className="fas fa-trash mr-2"></i>Clear All Data
							</button>
						</div>
						{importProgress && (
							<div className="p-4 bg-black/40 border border-gold/20 rounded-lg">
								<p className="text-off-white text-sm">{importProgress}</p>
							</div>
						)}
					</div>
				</section>
				) : null}

				{activeTab === 'categories' ? (
					<section className="bg-marble-black/80 border border-gold/30 rounded-xl p-4 sm:p-6">
						<SectionHeading
							icon="fa-folder-tree"
							title="All Categories"
							subtitle="Indented rows are subcategories of the row above them."
							badge={`${categoryCounts.main} main · ${categoryCounts.sub} sub`}
						/>
						<div className="mb-4 space-y-3">
							<div className="flex items-center gap-1 bg-black/40 border border-gold/20 p-1 rounded-lg w-fit">
								{[
									{ id: 'all', label: 'All', icon: 'fa-layer-group', count: categoryCounts.main + categoryCounts.sub },
									{ id: 'main', label: 'Main only', icon: 'fa-folder', count: categoryCounts.main },
									{ id: 'sub', label: 'Subs only', icon: 'fa-diagram-next', count: categoryCounts.sub }
								].map(opt => (
									<button
										key={opt.id}
										type="button"
										onClick={() => setListCategoryKind(opt.id)}
										className={`admin-tap px-3 py-1.5 rounded text-xs font-semibold transition-colors ${listCategoryKind === opt.id ? 'bg-gold text-black' : 'text-off-white hover:text-gold'}`}
									>
										<i className={`fas ${opt.icon} mr-1.5`}></i>{opt.label}
										<span className={listCategoryKind === opt.id ? 'opacity-70' : 'text-muted-gray'}> ({opt.count})</span>
									</button>
								))}
							</div>
							<div>
							<label className="block text-xs text-muted-gray mb-1.5">Filter by category/subcategory</label>
							<div className="flex gap-2">
							<select
								value={listFilterCategoryId}
								onChange={e => setListFilterCategoryId(e.target.value)}
								className="admin-input"
							>
								<option value="">All categories</option>
								{pathOptions.map(o => (
									<option key={o.id} value={o.id}>{hierarchyOptionLabel(o)}</option>
								))}
							</select>
							{listFilterCategoryId && (
								<button type="button" onClick={() => setListFilterCategoryId('')} className="shrink-0 px-3 rounded-lg border border-gold/20 text-muted-gray hover:text-gold hover:border-gold/50 transition-colors" title="Clear filter">
									<i className="fas fa-times"></i>
								</button>
							)}
							</div>
							{listFilterCategoryId && (
								<p className="text-xs text-muted-gray mt-1.5">Showing selected category + subcategories</p>
							)}
							</div>
						</div>
						<div className="admin-scroll space-y-1.5 max-h-[70vh] lg:max-h-[32rem] overflow-y-auto pr-1">
							{categoryRows.map(({ cat, depth, childCount, dishCount, orphaned }) => {
								const resolved = resolveCategoryImage(cat, cat.parentId ? categoriesById.get(cat.parentId) : null);
								const sourceBadge = {
									custom: { text: 'Custom image', className: 'bg-gold/15 text-gold border-gold/30' },
									default: { text: 'Default image', className: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30' },
									placeholder: { text: 'No image', className: 'bg-muted-gray/15 text-muted-gray border-muted-gray/30' }
								}[resolved.source];
								const menuPage = resolveMenuPageImages(cat, null);
								const showsMenuPhoto = menuPage.images.length > 0 && cat.menuPageMode !== 'products_only';
								// Indentation is suppressed when a filter already flattens the tree.
								// Kept small so deep rows still fit on a phone screen.
								const indentDepth = listCategoryKind === 'sub' ? 0 : depth;
								return (
								<div key={cat.id} className="flex items-stretch" style={{ paddingLeft: `${indentDepth * 0.75}rem` }}>
									{indentDepth > 0 ? (
										<span className="shrink-0 w-4 sm:w-5 self-stretch flex items-center justify-center text-violet-300/60" aria-hidden="true">
											<i className="fas fa-turn-up fa-rotate-90 text-xs"></i>
										</span>
									) : null}
									<div className={`flex-1 min-w-0 flex items-center gap-3 p-3 rounded-lg border transition-colors ${
										editingCat?.id === cat.id
											? 'bg-gold/10 border-gold/50'
											: depth === 0
												? 'bg-black/45 border-gold/15 hover:border-gold/40'
												: 'bg-black/25 border-violet-400/15 hover:border-violet-400/40'
									}`}>
									<img
										src={resolved.src}
										alt={getName(cat)}
										className={`${depth === 0 ? 'h-12 w-12' : 'h-10 w-10'} shrink-0 rounded-lg object-cover border border-gold/10`}
										loading="lazy"
										decoding="async"
										onError={(e) => {
											if (e.currentTarget?.dataset?.fallbackApplied) return;
											e.currentTarget.dataset.fallbackApplied = '1';
											e.currentTarget.src = PLACEHOLDER_IMAGE;
										}}
									/>
									<div className="flex-1 min-w-0">
										<div className="flex items-center gap-2 min-w-0">
											<i className={`fas ${depth === 0 ? 'fa-folder text-sky-300' : 'fa-diagram-next text-violet-300'} text-xs shrink-0`}></i>
											<p className={`truncate ${depth === 0 ? 'text-off-white font-semibold' : 'text-off-white/90 font-medium text-sm'}`}>
												{getName(cat) || <span className="text-red-400 italic">Untitled</span>}
											</p>
										</div>
										{depth > 0 ? (
											<p className="text-xs text-muted-gray truncate mt-0.5">
												inside {pathOptions.find(o => o.id === cat.parentId)?.label || 'Unknown'}
											</p>
										) : null}
										<div className="flex flex-wrap items-center gap-1.5 mt-1.5">
											<CategoryKindBadge depth={depth} orphaned={orphaned} />
											{childCount > 0 ? (
												<span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border bg-violet-500/10 text-violet-300/90 border-violet-400/25">
													<i className="fas fa-diagram-next"></i>{childCount} sub
												</span>
											) : null}
											<span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border bg-black/40 text-muted-gray border-gold/15">
												<i className="fas fa-utensils"></i>{dishCount} {dishCount === 1 ? 'dish' : 'dishes'}
											</span>
											<span className={`inline-block text-[10px] px-2 py-0.5 rounded-full border ${sourceBadge.className}`}>{sourceBadge.text}</span>
											{showsMenuPhoto ? (
												<span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border bg-amber-500/15 text-amber-300 border-amber-400/30" title="Tapping this shows a full-page menu photo">
													<i className="fas fa-image"></i>Menu photo
												</span>
											) : null}
										</div>
									</div>
									<div className="flex gap-2 shrink-0">
										<button onClick={() => handleEditCategory(cat)} title="Edit" className="h-9 w-9 sm:h-8 sm:w-8 shrink-0 flex items-center justify-center bg-gold/20 text-gold rounded-lg hover:bg-gold/35 transition-colors text-sm">
											<i className="fas fa-pen"></i>
										</button>
										<button onClick={() => handleDeleteCategory(cat.id)} title="Delete" className="h-9 w-9 sm:h-8 sm:w-8 shrink-0 flex items-center justify-center bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/35 transition-colors text-sm">
											<i className="fas fa-trash"></i>
										</button>
									</div>
									</div>
								</div>
								);
							})}
							{categoryRows.length === 0 ? (
								<p className="text-sm text-muted-gray text-center py-8">No categories match this filter.</p>
							) : null}
						</div>
					</section>
				) : null}

				{activeTab === 'dishes' ? (
					<section className="bg-marble-black/80 border border-gold/30 rounded-xl p-4 sm:p-6">
						<SectionHeading icon="fa-utensils" title="All Dishes" subtitle="Filter or search, then click the pencil to edit." badge={`${searchedProducts.length}`} />
						{orphanProducts.length > 0 ? (
							<div className="flex flex-wrap items-center gap-3 mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
								<i className="fas fa-triangle-exclamation text-red-400"></i>
								<p className="text-sm text-off-white/90 flex-1 min-w-[12rem]">
									<span className="text-red-300 font-semibold">{orphanProducts.length} dish{orphanProducts.length === 1 ? '' : 'es'}</span> {orphanProducts.length === 1 ? 'is' : 'are'} not linked to any existing category, so {orphanProducts.length === 1 ? 'it does' : 'they do'} not appear on the site. Edit each one and pick a category.
								</p>
								<button
									type="button"
									onClick={() => { setShowOrphansOnly(v => !v); setListFilterCategoryId(''); setListProductSearch(''); }}
									className="px-3 py-1.5 rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/15 transition-colors text-xs font-semibold"
								>
									{showOrphansOnly ? 'Show all dishes' : 'Show only these'}
								</button>
							</div>
						) : null}
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
							<div>
								<label className="block text-xs text-muted-gray mb-1.5">Filter by category</label>
								<select
									value={listFilterCategoryId}
									onChange={e => { setListFilterCategoryId(e.target.value); setShowOrphansOnly(false); }}
									className="admin-input"
									disabled={showOrphansOnly}
								>
									<option value="">All dishes</option>
									{pathOptions.map(o => (
										<option key={o.id} value={o.id}>{hierarchyOptionLabel(o)}</option>
									))}
								</select>
							</div>
							<div>
								<label className="block text-xs text-muted-gray mb-1.5">Search by name</label>
								<div className="relative">
									<i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-muted-gray text-xs"></i>
									<input
										value={listProductSearch}
										onChange={(e) => setListProductSearch(e.target.value)}
										placeholder="Type a dish name…"
										className="admin-input admin-input--icon"
									/>
								</div>
							</div>
						</div>
						{(listFilterCategoryId || listProductSearch || showOrphansOnly) ? (
							<div className="flex items-center gap-2 mb-3 text-xs text-muted-gray">
								<span>{showOrphansOnly ? 'Showing unlinked dishes only' : 'Filters active'}</span>
								<button
									type="button"
									onClick={() => { setListFilterCategoryId(''); setListProductSearch(''); setShowOrphansOnly(false); }}
									className="px-2 py-0.5 rounded border border-gold/25 text-gold hover:bg-gold/10 transition-colors"
								>
									Clear
								</button>
							</div>
						) : null}
						<div className="admin-scroll space-y-2 max-h-[70vh] lg:max-h-[32rem] overflow-y-auto pr-1">
							{searchedProducts.map(prod => (
								<div key={prod.id} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${editingProd?.id === prod.id ? 'bg-gold/10 border-gold/50' : 'bg-black/40 border-gold/10 hover:border-gold/30'}`}>
									<img
										src={(prod.imageUrl || '').trim() || PLACEHOLDER_IMAGE}
										alt={getName(prod)}
										className="h-12 w-12 shrink-0 rounded-lg object-cover border border-gold/10"
										loading="lazy"
										decoding="async"
										onError={(e) => {
											if (e.currentTarget?.dataset?.fallbackApplied) return;
											e.currentTarget.dataset.fallbackApplied = '1';
											e.currentTarget.src = PLACEHOLDER_IMAGE;
										}}
									/>
									<div className="flex-1 min-w-0">
										<p className="text-off-white font-medium truncate">{getName(prod) || <span className="text-red-400 italic">Untitled</span>}</p>
										<p className="text-xs text-gold">{prod.price} LEI</p>
										{(() => {
											const opt = prod.categoryId ? pathOptions.find(o => o.id === prod.categoryId) : null;
											if (!opt) {
												return (
													<p className="text-xs text-red-400 truncate">
														<i className="fas fa-triangle-exclamation mr-1"></i>
														{prod.categoryId ? 'Category was deleted — not shown on site' : 'No category — not shown on site'}
													</p>
												);
											}
											return (
												<p className="text-xs text-muted-gray truncate">
													<i className={`fas ${opt.depth ? 'fa-diagram-next text-violet-300' : 'fa-folder text-sky-300'} mr-1`}></i>
													{opt.label}
												</p>
											);
										})()}
									</div>
									<div className="flex gap-2 shrink-0">
										<button onClick={() => handleEditProduct(prod)} title="Edit" className="h-9 w-9 sm:h-8 sm:w-8 shrink-0 flex items-center justify-center bg-gold/20 text-gold rounded-lg hover:bg-gold/35 transition-colors text-sm">
											<i className="fas fa-pen"></i>
										</button>
										<button onClick={() => handleDeleteProduct(prod.id)} title="Delete" className="h-9 w-9 sm:h-8 sm:w-8 shrink-0 flex items-center justify-center bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/35 transition-colors text-sm">
											<i className="fas fa-trash"></i>
										</button>
									</div>
								</div>
							))}
							{searchedProducts.length === 0 ? (
								<p className="text-sm text-muted-gray text-center py-8">No dishes match this filter.</p>
							) : null}
						</div>
					</section>
				) : null}

				{activeTab === 'photos' ? (
					<PhotoOptimizer
						showToast={showToast}
						onFinished={() => setRefreshKey(prev => prev + 1)}
					/>
				) : null}

				{activeTab === 'settings' ? (
				<>
				{/* Footer Settings Section */}
				<section className="lg:col-span-2 bg-marble-black/80 border border-gold/30 rounded-xl p-4 sm:p-6">
					<SectionHeading icon="fa-address-card" title="Footer Settings" subtitle="Contact details, opening hours and social links shown on the site." />
					{footerSettings && (
						<form onSubmit={handleSaveFooter} className="space-y-6">
							{/* Contact Information */}
							<div className="space-y-4">
								<h3 className="font-cinzel text-lg text-gold/80">Contact Information</h3>
								<div>
									<label className="block text-xs text-muted-gray mb-1.5">Address</label>
									<input 
										value={footerAddress} 
										onChange={e => setFooterAddress(e.target.value)} 
										placeholder="Restaurant address" 
										className="admin-input" 
										required 
									/>
								</div>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="block text-xs text-muted-gray mb-1.5">Phone</label>
										<input 
											value={footerPhone} 
											onChange={e => setFooterPhone(e.target.value)} 
											placeholder="+40 XXX XXX XXX" 
											className="admin-input" 
											required 
										/>
									</div>
									<div>
										<label className="block text-xs text-muted-gray mb-1.5">Email</label>
										<input 
											type="email" 
											value={footerEmail} 
											onChange={e => setFooterEmail(e.target.value)} 
											placeholder="contact@restaurant.com" 
											className="admin-input" 
										/>
									</div>
								</div>
							</div>

							{/* Opening Hours */}
							<div className="space-y-4">
								<h3 className="font-cinzel text-lg text-gold/80">Opening Hours</h3>
								<div>
									<label className="block text-xs text-muted-gray mb-1.5">Monday - Thursday</label>
									<input 
										value={hoursMonThu} 
										onChange={e => setHoursMonThu(e.target.value)} 
										placeholder="11:00–22:00" 
										className="admin-input" 
										required 
									/>
								</div>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="block text-xs text-muted-gray mb-1.5">Friday - Saturday</label>
										<input 
											value={hoursFriSat} 
											onChange={e => setHoursFriSat(e.target.value)} 
											placeholder="11:00–23:00" 
											className="admin-input" 
											required 
										/>
									</div>
									<div>
										<label className="block text-xs text-muted-gray mb-1.5">Sunday</label>
										<input 
											value={hoursSun} 
											onChange={e => setHoursSun(e.target.value)} 
											placeholder="12:00–21:00" 
											className="admin-input" 
											required 
										/>
									</div>
								</div>
							</div>

							{/* Social Media Links */}
							<div className="space-y-4">
								<h3 className="font-cinzel text-lg text-gold/80">Social Media Links</h3>
								<p className="text-sm text-muted-gray">Leave empty to hide that social media icon</p>
								<div>
									<label className="block text-xs text-muted-gray mb-1.5">
										<i className="fab fa-instagram mr-2"></i>Instagram URL (optional)
									</label>
									<input 
										value={instagramUrl} 
										onChange={e => setInstagramUrl(e.target.value)} 
										placeholder="https://instagram.com/yourpage" 
										className="admin-input" 
									/>
								</div>
								<div>
									<label className="block text-xs text-muted-gray mb-1.5">
										<i className="fab fa-facebook mr-2"></i>Facebook URL (optional)
									</label>
									<input 
										value={facebookUrl} 
										onChange={e => setFacebookUrl(e.target.value)} 
										placeholder="https://facebook.com/yourpage" 
										className="admin-input" 
									/>
								</div>
								<div>
									<label className="block text-xs text-muted-gray mb-1.5">
										<i className="fab fa-tiktok mr-2"></i>TikTok URL (optional)
									</label>
									<input 
										value={tiktokUrl} 
										onChange={e => setTiktokUrl(e.target.value)} 
										placeholder="https://tiktok.com/@yourpage" 
										className="admin-input" 
									/>
								</div>
							</div>

							<div className="flex justify-end pt-2 border-t border-gold/10">
								<button type="submit" className="px-6 py-2.5 bg-gold text-black rounded-lg hover:bg-deep-gold transition-colors font-semibold">
									<i className="fas fa-save mr-2"></i>Save Footer Settings
								</button>
							</div>
						</form>
					)}
				</section>

				{/* App UI Settings Section */}
				<section className="lg:col-span-2 bg-marble-black/80 border border-gold/30 rounded-xl p-4 sm:p-6">
					<SectionHeading icon="fa-sliders" title="App UI Settings" subtitle="Global display options for the customer-facing menu." />
					<form onSubmit={handleSaveAppSettings} className="space-y-4">
						<div className="bg-black/40 border border-gold/20 rounded-lg p-4">
							<p className="text-off-white font-medium">Product placeholder image</p>
							<p className="text-xs text-muted-gray mt-1">Controls whether products without images show a placeholder image everywhere.</p>
							<label className="flex items-center gap-2 mt-3 text-sm text-off-white cursor-pointer">
								<input
									type="checkbox"
									className="admin-check"
									checked={!!showProductPlaceholderImage}
									onChange={(e) => setShowProductPlaceholderImage(e.target.checked)}
								/>
								Show placeholder for missing/broken product images
							</label>
						</div>
						<div className="flex justify-end pt-2 border-t border-gold/10">
							<button type="submit" className="px-6 py-2.5 bg-gold text-black rounded-lg hover:bg-deep-gold transition-colors font-semibold">
								<i className="fas fa-save mr-2"></i>Save App UI Settings
							</button>
						</div>
					</form>
				</section>
				</>
				) : null}
				</div>
			</main>
	{ToastComponent}
</div>
);
}
